<!--
 * @Author: WangWenqi
 * @Date: 2024-12-24 14:52:01
 * @Description: 合同模版新增查看-详情
-->
<template>
  <BaseForm
    ref="formRef"
    :model="formData"
    label-position="top"
    :rules="rules"
    v-if="!disabled || detail.id"
  >
    <BaseFormItem
      style="box-sizing: border-box; padding: 0 20px"
      prop="contractTemplateUrl"
      label="签署文件"
    >
      <BaseUpload
        v-xrk-loading="uploading"
        style="width: calc((100% - 20px) / 2)"
        ref="uploadRef"
        :disabled="!needAgainEdit && disabled"
        v-model:fileList="formData.contractTemplateUrl"
        :http-request="httpRequest"
        :onError="onError"
        :onChange="uploadChange"
        :auto-upload="true"
        :multiple="false"
        :accept="['.pdf']"
        :limit="1"
        :limitSize="100"
        :drag="false"
        btnText="添加签署文件"
        :uploadBtnProps="{ limitOverHide: true }"
        @preview="handlePreview"
      >
        <template #tip v-if="formData.contractTemplateUrl?.length < 1">
          只支持 PDF 文件，单份文件小于100M
        </template>
      </BaseUpload>
    </BaseFormItem>

    <div class="contract-detail__form-item3">
      <BaseFormItem :label="`${prefix}类型`" prop="contractType" required>
        <BaseSelect
          style="width: 100%; margin-top: 3px"
          :placeholder="`请选择${prefix}类型`"
          :options="CONTRACT_TYPE_OPT"
          v-model="formData.contractType"
          :disabled="true"
        ></BaseSelect>
      </BaseFormItem>
      <BaseFormItem
        :label="`${prefix}名称`"
        prop="contractTemplateName"
        required
      >
        <BaseInput
          style="width: 100%"
          maxlength="50"
          show-word-limit
          :placeholder="`请输入${prefix}名称`"
          :disabled="disabled"
          v-model="formData.contractTemplateName"
        ></BaseInput>
      </BaseFormItem>
      <BaseFormItem label="合同有效期" prop="validity" required>
        <BaseDatepickerLongterm
          style="width: 100%; margin-top: 1px"
          startPlaceholder="有效期始"
          :disabled="disabled"
          endPlaceholder="有效期止"
          v-model:value="formData.validity"
          @update:value="handleValidityChange"
          :disabled-date="disabledDate"
        />
      </BaseFormItem>
      <BaseFormItem
        v-if="contractType !== ContractType.CHANNEL"
        label="合同属性"
        prop="contractAttributes"
        required
      >
        <BaseRadioGroup
          :disabled="disabled"
          v-model="formData.contractAttributes"
        >
          <BaseRadio
            v-for="item in TEMPLATE_TYPE"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </BaseRadio>
        </BaseRadioGroup>
      </BaseFormItem>
      <BaseFormItem
        v-if="platformNameSelectShow"
        label="平台标识"
        prop="indiePlatformId"
        required
      >
        <BaseSelect
          :init="true"
          v-model="formData.indiePlatformId"
          :disabled="disabled"
          style="width: 100%"
          clearable
          valueKey="indiePlatformId"
          labelKey="platformName"
          filterUseRemote
          :remoteMethod="platformMethod"
          placeholder="请选择平台标识"
        ></BaseSelect>
      </BaseFormItem>
    </div>

    <div style="height: 16px; background: #f2f4f8"></div>

    <div class="title_with_line">设置签署方式</div>
    <BaseFormItem
      v-if="IS_PLATFORM_TYPE_1"
      style="box-sizing: border-box; padding: 0 20px"
      label="交易结构"
      prop="taxPattern"
      required
    >
      <BaseRadioGroup
        :disabled="disabled"
        v-model="formData.taxPattern"
        @change="handleTaxPatternChange"
      >
        <BaseRadio
          v-for="item in taxPatternMap"
          :key="item.value"
          :value="item.value"
        >
          {{ item.label }}
        </BaseRadio>
      </BaseRadioGroup>
    </BaseFormItem>
    <div class="contract-detail__form-item2">
      <BaseFormItem
        label="参与方"
        prop="participantA"
        :rules="[
          {
            required: true,
            message: '请选择参与方',
            trigger: 'change'
          }
        ]"
      >
        <BaseSelect
          :key="getPartAlist.length"
          clearable
          placeholder="请选择"
          :filterable="false"
          :options="getPartAlist"
          :init="true"
          labelKey="name"
          valueKey="id"
          v-model="formData.participantA"
          :disabled="disabled || participantsPartyAOpt?.length === 1"
          @change="
            val => {
              if (
                val &&
                !getPartBlist.some(item => item.id === formData.participantB)
              ) {
                formData.participantB = '';
              }
              formData.firstPartyId = '';
              formRef.validateField('participantA');
            }
          "
        ></BaseSelect>
      </BaseFormItem>
      <BaseFormItem
        label=""
        prop="participantB"
        :rules="[
          {
            required: true,
            message: '请选择参与方',
            trigger: 'change'
          }
        ]"
      >
        <BaseSelect
          clearable
          placeholder="请选择"
          :filterable="false"
          :key="getPartBlist.length"
          :options="getPartBlist"
          :init="true"
          labelKey="name"
          valueKey="id"
          v-model="formData.participantB"
          :disabled="disabled || participantsPartyBOpt?.length === 1"
          @change="
            val => {
              if (
                val &&
                !getPartAlist.some(item => item.id === formData.participantA)
              ) {
                formData.participantA = '';
              }
              formData.secondPartyId = '';
              formRef.validateField('participantB');
            }
          "
        ></BaseSelect>
      </BaseFormItem>
    </div>
    <div class="contract-detail__form-item2">
      <BaseFormItem
        prop="firstPartyId"
        :rules="[
          {
            required: true,
            message: '请选择签署方',
            trigger: 'change'
          }
        ]"
        label="签署方"
      >
        <BaseSelect
          clearable
          placeholder="请选择甲方"
          :filterUseRemote="true"
          :init="true"
          :filter="true"
          labelKey="name"
          valueKey="id"
          :remoteMethod="async (enterpriseName: string) => {
            
            const party = formData.participantA;
            const reqParams = {
              index:0,
              type: props.pageType,
              entpName: 
                disabled && detail.firstPartyId === 'all'
                  ? detail.firstPartyName
                  : enterpriseName,
              participantValue: party,
              contractType: formData.contractType,
              entpId:
                detail.firstPartyId === 'all'
                  ? ''
                  : detail.firstPartyId,
                  taxPattern:formData.taxPattern
            };
            if (!isInit[0]) {
              delete reqParams.entpId;
            } else {
              delete reqParams.entpName;
            }
            isInit[0] = false;
            if (!party) {
              return {
                data: {
                  list: []
                }
              };
            }
            const entpInfo = disabled?{
              name:detail.firstPartyName,
              id:detail.firstPartyId
            }:{}
            // 签署方-甲方不展示根据模板选择
            const res = await getEnterpriseList(reqParams,entpInfo);

            return res;
          
          }"
          v-model="formData.firstPartyId"
          :disabled="disabled"
          @visibleChange="
            visible => {
              const party = formData.participantA;
              if (visible && !party) {
                XrkMessage.error('请先选择参与方');
              }
            }
          "
          @change="
            () => {
              formRef.validateField('firstPartyId');
            }
          "
        ></BaseSelect>
      </BaseFormItem>
      <BaseFormItem
        prop="secondPartyId"
        :rules="[
          {
            required: true,
            message: '请选择签署方',
            trigger: 'change'
          }
        ]"
        label=""
      >
        <BaseSelect
          clearable
          placeholder="请选择乙方"
          :filterUseRemote="true"
          :init="true"
          :filter="true"
          labelKey="name"
          valueKey="id"
          :remoteMethod="async (enterpriseName: string) => {
            
            const party = formData.participantB;
            const reqParams = {
              index:1,
              type: props.pageType,
              entpName: 
                disabled && detail.secondPartyId === 'all'
                  ? detail.secondPartyName
                  : enterpriseName,
              participantValue: party,
              contractType: formData.contractType,
              entpId:
                detail.secondPartyId === 'all'
                  ? ''
                  : detail.secondPartyId,
                  taxPattern:formData.taxPattern,
                  businessParkId: formData.businessParkId
            };
            if (!isInit[1]) {
              delete reqParams.entpId;
            } else {
              delete reqParams.entpName;
            }
            isInit[1] = false;
            if (!party) {
              return {
                data: {
                  list: []
                }
              };
            }
             // 合同模板-市场服务人员合同, 乙方写死 根据模板选择
            if (
              formData.contractType === ContractType.MARKET_PERSON &&
              props.pageType === ContractMenuType.TEMPLATE
            ) {
              return {
                data: {
                  list: [
                    {
                      id: 'all',
                      name: '根据模板选择'
                    }
                  ]
                }
              };
            }
            const entpInfo = disabled?{
              name:detail.secondPartyName,
              id:detail.secondPartyId
            }:{}
            // 签署方-甲方不展示根据模板选择
            const res = await getEnterpriseList(reqParams,entpInfo);

            return res;
          
          }"
          v-model="formData.secondPartyId"
          :disabled="
            disabled ||
            (contractType === ContractType.AGENCY_SERVICE &&
              !formData.businessParkId)
          "
          @visibleChange="
            visible => {
              const party = formData.participantB;
              if (visible && !party) {
                XrkMessage.error('请先选择参与方');
              }
            }
          "
          @change="
            () => {
              formRef.validateField('secondPartyId');
            }
          "
        ></BaseSelect>
      </BaseFormItem>
      <BaseFormItem
        v-if="
          formData.contractType === ContractType.AGENCY_SERVICE &&
          formData.taxPattern === TradeStructureMap.PARK_INDIVIDUAL &&
          [6, 8].includes(formData.participantA)
        "
        label=""
        prop="businessParkId"
        :rules="[
          {
            required: true,
            message: '请选择所属园区',
            trigger: 'change'
          }
        ]"
      >
        <BaseSelect
          :disabled="disabled || !formData.firstPartyId"
          clearable
          placeholder="请选择所属园区"
          :filterable="false"
          :init="true"
          :remoteMethod="parkRemoteMethod"
          labelKey="businessParkName"
          valueKey="businessParkId"
          v-model="formData.businessParkId"
        ></BaseSelect>
      </BaseFormItem>
    </div>

    <div class="contract-detail__form-item2">
      <BaseFormItem
        v-if="props.pageType === ContractMenuType.MANAGEMENT"
        label="签署状态"
        prop="firstStatus"
        :rules="[
          {
            required: true,
            message: '请选择签署状态',
            trigger: 'change'
          }
        ]"
      >
        <BaseRadioGroup
          v-model="formData.firstStatus"
          :disabled="!needAgainEdit && disabled"
        >
          <BaseRadio :value="2">已签</BaseRadio>
          <BaseRadio :value="0">未签</BaseRadio>
        </BaseRadioGroup>
      </BaseFormItem>
      <BaseFormItem
        v-if="props.pageType === ContractMenuType.MANAGEMENT"
        label=""
        prop="secondStatus"
        :rules="[
          {
            required: true,
            message: '请选择签署状态',
            trigger: 'change'
          }
        ]"
      >
        <BaseRadioGroup
          v-model="formData.secondStatus"
          :disabled="!needAgainEdit && disabled"
        >
          <BaseRadio :value="2">已签</BaseRadio>
          <BaseRadio :value="0">未签</BaseRadio>
        </BaseRadioGroup>
      </BaseFormItem>
    </div>

    <BaseFormItem
      v-if="
        IS_PLATFORM_TYPE_1 &&
        formData.taxPattern === 4 &&
        formData.contractType === ContractType.MARKET_PERSON &&
        props.pageType === ContractMenuType.TEMPLATE
      "
      style="box-sizing: border-box; padding: 0 20px"
      label="完税类型"
      prop="taxBroadType"
      required
    >
      <BaseRadioGroup :disabled="disabled" v-model="formData.taxBroadType">
        <BaseRadio
          v-for="item in TAX_TYPE"
          :key="item.value"
          :value="item.value"
        >
          {{ item.label }}
        </BaseRadio>
      </BaseRadioGroup>
    </BaseFormItem>

    <slot :formData="formData"></slot>
    <div style="height: 16px; background: #f2f4f8"></div>
  </BaseForm>
  <div
    v-else
    v-xrk-loading="true"
    style="width: 100%; height: 400px"
    element-loading-text="加载中..."
  ></div>
</template>

<script lang="tsx" setup>
import {
  CONTRACT_TYPE,
  ContractMenuType,
  ContractType,
  taxPatternSelect,
  TradeStructureMap,
  TEMPLATE_TYPE,
  TAX_PATTERN,
  TAX_TYPE
} from './../constants/index';
import { computed, ref, toRefs, watch, nextTick } from 'vue';
import {
  BaseForm,
  BaseInput,
  BaseSelect,
  BaseFormItem,
  BaseUpload,
  XrkMessage,
  BaseRadioGroup,
  BaseRadio
} from 'xrk-components';
import { BaseDatepickerLongterm } from '@/components/base/base-datepicker-longterm/index';
import { uploadApi } from '@data-source/common/apis';
import {
  getEnterpriseList,
  getPartOptList,
  platformDicList,
  parkList
} from './../data-source/apis';
import { preview } from '@/utils/file';
import { IS_PLATFORM_TYPE_1 } from '@/config';

const props = withDefaults(
  defineProps<{
    /** 是否只读 */
    disabled?: boolean;
    contractType: any;
    detail?: any;
    prefix: string;
    /** 页面来源 */
    pageType: number;
  }>(),
  {
    disabled: () => true,
    prefix: ''
  }
);

const { disabled, contractType, detail } = toRefs(props);

let isInit = {
  0: true,
  1: true
};

/** 甲方 */
const participantsPartyAOpt = ref([]);
/** 乙方 */
const participantsPartyBOpt = ref([]);

const uploadRef = ref();
const formRef = ref();

const uploading = ref(false);

const rules = {
  contractTemplateName: [
    {
      required: true,
      message: `${props.prefix}名称不能为空`,
      trigger: 'blur'
    }
  ],
  contractType: [
    {
      required: true,
      message: `${props.prefix}类型不能为空`,
      trigger: 'change'
    }
  ],
  contractTemplateUrl: [
    {
      required: true,
      validator: (_rule: any, value: any, callback: any) => {
        if (!value?.length) {
          callback(new Error('请上传签署文件'));
        } else {
          callback();
        }
      },
      trigger: 'change'
    }
  ],
  contractAttributes: [
    {
      required: true,
      message: `请选择合同属性`,
      trigger: 'change'
    }
  ],
  indiePlatformId: [
    {
      required: true,
      message: `请选择平台标识`,
      trigger: 'change'
    }
  ],
  taxBroadType: [
    {
      required: true,
      message: `请选择完税类型`,
      trigger: 'change'
    }
  ],

  taxPattern: [
    {
      required: true,
      message: `请选择交易结构`,
      trigger: 'change'
    }
  ],
  validity: [
    {
      required: true,
      validator: (_rule: any, value: any, callback: any) => {
        if (!value?.length) {
          callback(new Error('合同有效期不能为空'));
        } else {
          callback();
        }
      },
      trigger: 'change'
    }
  ]
};

const getPartAlist = computed(() => {
  const { participantB } = formData.value;
  // 甲方选项
  return (
    participantsPartyBOpt.value?.find(item => item.id === participantB)
      ?.children || []
  );
});

const getPartBlist = computed(() => {
  const { participantA } = formData.value;
  // 乙方选项
  return (
    participantsPartyAOpt.value?.find(item => item.id === participantA)
      ?.children || []
  );
});
/**
 * @description: 平台标识下拉
 * @param {*} v
 * @returns {*}
 */
const platformMethod = async (platformName?: string) => {
  return await platformDicList({ platformName });
};

const formData = ref<any>({
  contractType: contractType.value,
  validity: [],
  contractTemplateUrl: [],
  contractTemplateName: '',
  taxPattern: '',
  taxBroadType: IS_PLATFORM_TYPE_1 ? null : 2
});

const needAgainEditContractTypes = [
  ContractType.MARKET_PERSON,
  ContractType.MARKET,
  ContractType.CHANNEL
];

const needAgainEdit = computed(() => {
  if (
    orignSignStatus.value.firstStatus === 2 &&
    orignSignStatus.value.secondStatus === 2
  ) {
    return false;
  }
  return (
    needAgainEditContractTypes.includes(+contractType.value) &&
    props.pageType === ContractMenuType.MANAGEMENT
  );
});

const platformNameSelectShow = computed(() => {
  // 平台标识下拉 只在向日葵并且是合同管理的市场服务合同展示
  return (
    IS_PLATFORM_TYPE_1 &&
    props.pageType === ContractMenuType.MANAGEMENT &&
    [ContractType.MARKET].includes(contractType.value)
  );
});

const taxPatternMap = computed(() => {
  const _TAX_PATTERN = JSON.parse(JSON.stringify(TAX_PATTERN));
  return taxPatternSelect[props.pageType][contractType.value].map(item => {
    return _TAX_PATTERN[item];
  });
});
// 默认选中第一个
formData.value.taxPattern = IS_PLATFORM_TYPE_1
  ? taxPatternMap.value[0].value
  : 4; // 独立项目只有运营基地 写死

const disabledDate = time => {
  if (props.pageType === ContractMenuType.MANAGEMENT) {
    return;
  }
  return time.getTime() < new Date().getTime() - 8.64e7;
};

const CONTRACT_TYPE_OPT = computed(() => {
  let types = [];
  if (props.pageType === ContractMenuType.TEMPLATE) {
    types = [
      ContractType.PLATFORM,
      ContractType.PROJECT,
      ContractType.MARKET_PERSON,
      ContractType.AGENCY_SERVICE
    ];
  } else {
    types = [
      ContractType.PLATFORM,
      ContractType.MARKET,
      ContractType.CHANNEL,
      ContractType.MARKET_PERSON,
      ContractType.AGENCY_SERVICE
    ];
  }
  return CONTRACT_TYPE.filter(item => types.includes(item.value));
});
const getLastStr = str => {
  const match = str.match(/.*\/([^/]*)$/);
  return match ? match[1] : '';
};
/**
 * @description: 园区下拉
 * @param {*} v
 * @returns {*}
 */
const parkRemoteMethod = async () => {
  return parkList({ proxySupplierId: formData.value.firstPartyId });
};
// 获取参与方选项
const getParticipantOpt = async val => {
  participantsPartyAOpt.value = await getPartOptList({
    type: val,
    typeOfPartyAAndPartyB: 1,
    source: +props.pageType,
    taxPattern: formData.value.taxPattern
  });
  participantsPartyBOpt.value = await getPartOptList({
    type: val,
    typeOfPartyAAndPartyB: 2,
    source: +props.pageType,
    taxPattern: formData.value.taxPattern
  });
  await nextTick();
  // 从查看进来的话不设置默认值
  if (disabled.value) {
    return;
  }
  formData.value.participantA = participantsPartyAOpt.value[0].id;
  formData.value.participantB = participantsPartyBOpt.value[0].id;
};

const orignSignStatus = ref({
  firstStatus: null,
  secondStatus: null
});

watch(
  () => detail.value,
  val => {
    if (!Object.keys(val || {}).length) {
      return;
    }
    const {
      contractTemplateUrl,
      validityStart,
      validityEnd,
      urlFileName,
      secondPartyId
    } = val;
    formData.value = {
      ...formData.value,
      ...val,
      contractTemplateUrl: contractTemplateUrl
        ? [
            {
              name: urlFileName || getLastStr(contractTemplateUrl),
              url: contractTemplateUrl
            }
          ]
        : [],
      validity: validityStart ? [validityStart, validityEnd] : []
    };
    if (props.pageType === ContractMenuType.TEMPLATE) {
      formData.value.secondPartyId = secondPartyId ? secondPartyId : 'all';
    }
    orignSignStatus.value = JSON.parse(
      JSON.stringify({
        firstStatus: val.firstStatus,
        secondStatus: val.secondStatus
      })
    );
    nextTick(() => {
      getParticipantOpt(contractType.value);
    });
  },
  {
    immediate: true
  }
);
if (!props.disabled) getParticipantOpt(contractType.value);
const onError = () => {
  XrkMessage.error('只支持 PDF 文件，单份文件小于 100M');
};

const httpRequest = async ({ file }) => {
  uploading.value = true;
  return uploadApi({ file }).then(
    res => {
      formData.value.urlFileName = res.data.fileName;
      uploading.value = false;
      return res;
    },
    err => {
      uploading.value = false;
      return Promise.reject(err);
    }
  );
};
const uploadChange = () => {
  formRef.value.validateField('contractTemplateUrl');
};

const handleTaxPatternChange = () => {
  formData.value.participantA = '';
  formData.value.participantB = '';
  formData.value.firstPartyId = '';
  formData.value.secondPartyId = '';
  getParticipantOpt(formData.value.contractType);
};

const handleValidityChange = () => {
  formRef.value.validateField('validity');
};

const handlePreview = async () => {
  const url = formData.value.contractTemplateUrl[0].url;
  preview(url);
};

defineExpose({
  submit: async () => {
    await formRef.value.validate();
    const {
      validity: [validityStart, validityEnd],
      firstPartyId,
      secondPartyId,
      contractTemplateUrl
    } = formData.value;
    if (
      !contractTemplateUrl.length ||
      contractTemplateUrl.some(item => !item.url)
    ) {
      XrkMessage.error('合同模板上传中，请稍后');
      throw new Error('合同模板上传中，请稍后');
    }
    const result = {
      ...formData.value,
      validityStart,
      validityEnd,
      contractTemplateUrl: contractTemplateUrl[0].url,
      firstPartyId: firstPartyId === 'all' ? null : firstPartyId,
      secondPartyId: secondPartyId === 'all' ? null : secondPartyId
    };
    delete result.validity;
    return result;
  }
});
</script>
<style lang="scss" scoped>
@import '@/styles/ui.scss';

.contract-detail__form-item3 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0 24px;
  box-sizing: border-box;
  padding: 0 20px;
}

.contract-detail__form-item2 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  align-items: flex-end;
  gap: 0 24px;
  box-sizing: border-box;
  padding: 0 20px;
}
</style>
